
const LessonBenefits = () => {
  const benefits = [
    {
      icon: 'ri-user-heart-line',
      title: 'Personalized Attention',
      description: 'Get focused, one-on-one attention that addresses your specific challenges and goals',
      color: 'blue'
    },
    {
      icon: 'ri-mic-line',
      title: 'Speaking Practice',
      description: 'Practice real conversations and improve your pronunciation with immediate feedback',
      color: 'green'
    },
    {
      icon: 'ri-question-answer-line',
      title: 'Ask Questions Freely',
      description: 'No question is too small - get clarification on anything you don\'t understand',
      color: 'purple'
    },
    {
      icon: 'ri-target-line',
      title: 'Goal-Oriented Learning',
      description: 'Work towards specific objectives like job interviews, travel, or academic goals',
      color: 'orange'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Flexible Scheduling',
      description: 'Book lessons at times that fit your busy schedule - evenings and weekends available',
      color: 'pink'
    },
    {
      icon: 'ri-file-text-line',
      title: 'Custom Materials',
      description: 'Receive personalized exercises and materials based on your learning progress',
      color: 'indigo'
    }
  ];

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      purple: 'bg-purple-100 text-purple-600',
      orange: 'bg-orange-100 text-orange-600',
      pink: 'bg-pink-100 text-pink-600',
      indigo: 'bg-indigo-100 text-indigo-600',
    };
    return colorMap[color as keyof typeof colorMap];
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Private Lessons?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              While self-learning is great, sometimes you need that personal touch to break through barriers 
              and accelerate your progress. Here's what makes our private lessons special:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow">
                <div className={`w-16 h-16 ${getColorClasses(benefit.color)} rounded-xl flex items-center justify-center mb-6`}>
                  <i className={`${benefit.icon} text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>

          {/* What to Expect */}
          <div className="mt-16 bg-white rounded-2xl p-8 shadow-sm">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">What to Expect in Your Lesson</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-bold">1</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Warm-up Chat</h4>
                <p className="text-sm text-gray-600">Start with casual conversation to get comfortable</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-bold">2</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Focused Practice</h4>
                <p className="text-sm text-gray-600">Work on specific areas you want to improve</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-bold">3</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Interactive Exercises</h4>
                <p className="text-sm text-gray-600">Practice with real-world scenarios and role-plays</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-bold">4</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Feedback & Tips</h4>
                <p className="text-sm text-gray-600">Get personalized advice for continued improvement</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LessonBenefits;
